import type { MaybeRefOrGetter } from "vue";
import { toValue, watch } from "vue";

// The observer reports only threshold crossings; 1% steps notice the first pixels the cell loses.
const VISIBILITY_STEPS = Array.from({ length: 101 }, (_, step) => step / 100);

export interface CloseWhenAnchorClippedOptions {
  open: MaybeRefOrGetter<boolean>;
  anchor: MaybeRefOrGetter<HTMLElement | null>;
  close: () => void;
}

/**
 * Calls `close` once the open popup's anchor shows less than it has since the popup opened, e.g.
 * the grid scrolls the header cell towards its edge. The popup sits in the top layer, so nothing
 * clips it with the cell: it would follow the cell out of the grid, over the navigation next to it.
 * An anchor already cut off at opening keeps the popup open until it loses more.
 */
export function useCloseWhenAnchorClipped(options: CloseWhenAnchorClippedOptions) {
  if (typeof IntersectionObserver === "undefined") return;

  watch(
    [() => toValue(options.open), () => toValue(options.anchor)],
    ([open, anchor], _previous, onCleanup) => {
      if (!open || !anchor) return;

      let mostVisible = 0;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const { intersectionRatio } of entries) {
            if (intersectionRatio < mostVisible) {
              observer.disconnect();
              options.close();
              return;
            }
            mostVisible = intersectionRatio;
          }
        },
        { threshold: VISIBILITY_STEPS },
      );
      observer.observe(anchor);
      onCleanup(() => observer.disconnect());
    },
    { immediate: true },
  );
}
